import type { TestimonialReviewsV2Data, TestimonialReviewsV2Review } from './types'

const CUSTOMER_REVIEWS_TITLE = 'What our customers have to say'

/**
 * Shared Trustpilot quotes reused across category and product-page banks.
 * Keys are stable ids so banks can compose the same review without duplicating copy.
 */
const REVIEW_BY_KEY = {
    leslie: {
        title: 'Everything is upfront',
        description:
            'Everything is upfront and exactly what they state. No hidden costs or fees. Great experience!',
        name: 'Leslie',
    },
    thomas: {
        title: 'Continuity of care that feels one-on-one',
        description:
            'The continuity of care provided by the network of physicians and support staff makes me feel as if it\u2019s one-on-one.',
        name: 'Thomas LeBlanc',
    },
    andrew: {
        title: 'On time, knowledgeable, and thorough',
        description:
            'The doctor I had my appointment with was on time, extremely knowledgeable, and took his time getting to know me and my medical history.',
        name: 'Andrew',
    },
    misty: {
        title: 'Informative all the way through',
        description:
            'This was an awesome experience. I had labs to make sure I was a candidate and all the way through they were so informative.',
        name: 'Misty Holloway',
    },
    nichelle: {
        title: 'I felt heard and they followed through',
        description:
            'I felt heard and they followed through until my prior authorization was approved after it was denied. They maintained communication with me until I received my medication.',
        name: 'Nichelle C',
    },
    mya: {
        title: 'Got refills quickly and affordably',
        description: 'I needed medication refills, and they were able to fill them quickly and affordably.',
        name: 'Mya Harbison',
    },
    judy: {
        title: 'Quick and easy from booking to Zoom',
        description:
            'Everything was quick and easy from booking the blood work to our Zoom appointment. The doctor was pleasant and attentive.',
        name: 'Judy S',
    },
    cecily: {
        title: 'Helped me figure out what I actually needed',
        description:
            'The doctor helped me figure out what I actually needed instead of what I initially made the appointment for.',
        name: 'Cecily Brown',
    },
    samantha: {
        title: 'She went above and beyond',
        description:
            'She went above and beyond to make sure I was heard and she remembered me and my journey at each visit.',
        name: 'Samantha Barnard',
    },
    pamela: {
        title: 'Excellent, efficient and caring',
        description:
            'Excellent, efficient and caring medical professionals helped me negotiate some difficult medical situations, coordinating care, streamlining processes, and most important, giving me the best medical care.',
        name: 'Pamela',
    },
    debbies: {
        title: 'Excellent care and support',
        description:
            'I am receiving EXCELLENT care and support. Every questions answered efficiently and quickly just loving my experience here!!',
        name: 'Debbies',
    },
    john: {
        title: 'They took care of everything',
        description:
            'I went looking online for a GLP-1 doctor and found these people. They communicate very well and they took care of everything. I needed to get qualified for the Medicare bridge program. The staff is very courteous.',
        name: 'John',
    },
    steff: {
        title: 'Kind, thorough, and quick with referrals',
        description:
            'I called in with recent labs I\u2019d had done, and the doctor was very kind, thorough, and quick — called in all my previous medication right away, and set me up with a few referral doctors.',
        name: 'Steff',
    },
    kim: {
        title: 'Labs requested, script within 5 days',
        description: 'Prompt, serious and to the point service. Requested labs and I had my script within 5 days.',
        name: 'Kim',
    },
    tatum: {
        title: 'Finally getting labs and referrals',
        description:
            'I\u2019m finally getting the labs and referrals to the specialist I\u2019ve been trying to get into with my prior MD for the last 3 years and I\u2019m finally getting answers.',
        name: 'Tatum',
    },
    scott: {
        title: 'Amazing value for meds and labs',
        description:
            'I needed my blood pressure and diabetes medications renewed, and without insurance right now, it was an amazing value to see a knowledgeable person and get my labs done at a great price and in a timely manner.',
        name: 'Scott',
    },
    stacie: {
        title: 'Labs drawn locally, results in two days',
        description:
            'I saw the doctor on a video call, and he ordered the labs I needed — drawn locally the next day, with the insurance handled through the diagnostic center. I had my results two days later, with a follow-up already scheduled.',
        name: 'Stacie',
    },
    andrewLabs: {
        title: 'Scheduling labs was a breeze',
        description:
            'They made scheduling my labs and getting my prescription a breeze and it was all affordable. Definitely using them again in the future!',
        name: 'Andrew',
    },
    liane: {
        title: 'Getting Zepbound through Lilly Direct was so simple',
        description:
            'General Medicine made getting my Zepbound medication through Lilly Direct so simple and they had same day appointments! They billed my insurance so the appointment cost me nothing out of pocket. I highly recommend this company! Thank you for your help!',
        name: 'Liane',
    },
    christopher: {
        title: 'They were incredible during my weight loss journey',
        description:
            'General Medicine has been absolutely incredible so far during my weight loss journey! Their response and prompt follow-up is appreciated. I would highly recommend to anyone looking to improve their health and start their weight loss journey!',
        name: 'Christopher R',
    },
    christopherMeds: {
        title: 'Knowledgeable, friendly, and accommodating',
        description:
            'I needed a refill on some meds. They also took care of my GLP-1s. And all this was covered by my insurance. The doctor was knowledgeable, friendly, and accommodating. I will use General Medicine for any future nonemergency medical needs.',
        name: 'Christopher',
    },
    jen: {
        title: 'Within a few hours... I was prescribed Mounjaro',
        description:
            'My in person primary care doctor dropped the ball on getting my Ozempic pre-authorized with my new insurance, and essentially gave up after the first denial... I scheduled with General Medicine instead. Within a few hours I not only paid a lower copay, I was prescribed Mounjaro, which is better suited to treat my type 2 and high cholesterol issues.',
        name: 'Jen',
    },
    rosa: {
        title: 'Felt like they sincerely cared',
        description:
            'I had been without my thyroid meds for far too long... The service they provided me from the moment I first made contact with them was not only very professional, but they were so very kind that I felt as though they knew me. They made me feel like they sincerely cared, like I had worth and like I really mattered to them. I felt like I had been going to them for years.',
        name: 'Rosa',
    },
    cassie: {
        title: 'Received meds quickly',
        description:
            'I signed up for an appointment the next morning, had meds that same day. Doctor was very knowledgeable and friendly. They have provided great service with fast response times.',
        name: 'Cassie',
    },
    james: {
        title: 'They were really on my side when I was in a tight spot',
        description:
            'I called them because my prescription for diabetes medicine had run out and I was transitioning between two HMO\u2019s. I was in a jam - neither of my HMO\u2019s would prescribe anything. I spoke with the doctor online at General Medicine and they made it happen. I got my medicine. I would recommend them, they were really on my side when I was in a tight spot.',
        name: 'James',
    },
    donna: {
        title: 'Prescription filled in no time',
        description:
            'I am without insurance and needed my prescription refilled. I found General Medicine and set up a text session. It was easy to set up, they were prompt and I had my prescription filled in no time. I am so glad I found them. It was the least expensive route I could find to keep my meds going without having to pay a ridiculous fee to see a Dr to get it refilled. I definitely would recommend General Medicine.',
        name: 'Donna',
    },
    baylee: {
        title: 'Easy and Quick!',
        description:
            'Needed a prescription over a weekend I couldn\u2019t get in to see my primary care. I scheduled a virtual visit through my insurance and paid my normal co pay. The NP I saw was amazing, very helpful and informative. At the end of the visit, she finished my care plan within the hour. Once that was done I selected my pharmacy and had my meds! The platform is very easy to use and understand.',
        name: 'Baylee',
    },
    kara: {
        title: 'Easy and Accessible',
        description:
            'Had a great experience with General Med. I had a horrific infection from a piercing and was able to get meds quicker than from my doctors office. They texted and helped when the pharmacy was having technical problems. So I didn\u2019t have to wait hours to get help from someone. Very easy and accessible.',
        name: 'Kara',
    },
    julie: {
        title: 'Very understanding of my needs',
        description:
            'My doctor was very understanding of my needs. He  increased my Wegovy dose, which was helpful and is helping me now. Because I am pre-diabetic, High blood pressure and also have heart disease, this extra\u00a0weight\u00a0is fatal to my health. Now I am losing\u00a0weight\u00a0in a healthy way.',
        name: 'Julie',
    },
    jacquelyn: {
        title: 'Easy and thorough',
        description:
            'I needed medication at a time when my doctor was not in the office. This was such a seamless and easy experience. I set up an appointment within a couple of hours of filling out some information. I had a video call with a doctor who asked very thoughtful and thorough questions. A prescription was sent to my pharmacy that day. It was a great experience. I don\u2019t look forward to being ill again, but to know General Medicine is there when I do is very comforting.',
        name: 'Jacquelyn',
    },
    freddy: {
        title: 'Absolute lifesaver',
        description:
            'Absolutely life saver. My regular doctor completely let me down with a prescription refill request and ignored me all week before I was going on holiday. I was able to book an appointment with general medicine within 30 mins and speak with a doctor to get my refill. She was thorough and efficient',
        name: 'Freddy',
    },
    kelly: {
        title: 'If it wasn\u2019t for General Medicine I would still be suffering',
        description:
            'I have been suffering with kidney stones for a good while now. I have tried to get in to see a urologist and the earliest appointment I can get is not until October. I used General Medicine, answered some questions and had a prescription that day. A week of taking the medication and I finally had some relief. If it wasn\u2019t for General Medicine I would still be suffering',
        name: 'Kelly',
    },
    camille: {
        title: 'Listened and responded right away',
        description:
            'The physician I was paired with listened to all of my concerns and responded right away. She was thorough, and I received my medication within hours.',
        name: 'Camille',
    },
    mark: {
        title: 'Prescription confirmed within 45 minutes',
        description:
            'I\u2019d run out of a medication while I was in the process of changing doctors. I provided some information, and my prescription was done and confirmed within 45 minutes.',
        name: 'Mark',
    },
    louis: {
        title: 'Thorough history check and a prompt prior authorization',
        description:
            'They were very thorough in checking my medical history for any possible problems with the new medication, and very prompt in handling the prior authorization with the pharmacy and the insurance carrier.',
        name: 'Louis',
    },
} as const satisfies Record<string, TestimonialReviewsV2Review>

type ReviewKey = keyof typeof REVIEW_BY_KEY

function bank(title: string, keys: readonly ReviewKey[]): TestimonialReviewsV2Data {
    return {
        title,
        reviews: keys.map((key) => REVIEW_BY_KEY[key]),
    }
}

/** Reviews shown on condition, medication, and visit product pages. */
const PRODUCT_PAGE_REVIEW_KEYS: ReviewKey[] = [
    'leslie',
    'thomas',
    'andrew',
    'misty',
    'nichelle',
    'mya',
    'judy',
    'cecily',
    'samantha',
    'pamela',
    'debbies',
    'john',
]

/** Reviews shown on the labs category page and lab product pages. */
const LABS_REVIEW_KEYS: ReviewKey[] = [
    'misty',
    'steff',
    'kim',
    'tatum',
    'scott',
    'stacie',
    'andrewLabs',
]

/**
 * Trustpilot review banks for `testimonialReviewsV2`, keyed by rendered-service id.
 * Backend places `{ id, type: "testimonialReviewsV2" }`; the frontend looks up copy here.
 * `product-pages-stories` / `labs-stories` are also used directly on SKU PDPs.
 */
export const TESTIMONIAL_REVIEWS_V2_BY_ID: Record<string, TestimonialReviewsV2Data> = {
    'product-pages-stories': bank(CUSTOMER_REVIEWS_TITLE, PRODUCT_PAGE_REVIEW_KEYS),
    'labs-stories': bank(CUSTOMER_REVIEWS_TITLE, LABS_REVIEW_KEYS),
    'weight-loss-stories': bank('Weight loss stories from General Medicine', [
        'liane',
        'christopher',
        'leslie',
        'thomas',
        'andrew',
        'misty',
        'nichelle',
    ]),
    'womens-health-stories': bank('Women\u2019s health stories from General Medicine', [
        'nichelle',
        'mya',
        'judy',
        'cecily',
        'samantha',
        'pamela',
        'leslie',
    ]),
    'medications-stories': bank(CUSTOMER_REVIEWS_TITLE, [
        'leslie',
        'nichelle',
        'mya',
        'judy',
        'camille',
        'mark',
        'louis',
    ]),
}

/** SKU categories that show Trustpilot reviews instead of SkuTestimonials. */
export const PRODUCT_PAGE_TRUSTPILOT_CATEGORIES = new Set([
    'condition',
    'medication',
    'visit',
    'lab',
    'specialty',
])

/** Which Trustpilot bank to show on a SKU or medication product page. */
export type SkuPageTrustpilotVariant = 'product' | 'lab' | 'medications'

const SECTION_ID_BY_VARIANT: Record<SkuPageTrustpilotVariant, string> = {
    product: 'product-pages-stories',
    lab: 'labs-stories',
    medications: 'medications-stories',
}

/** Trustpilot bank variant for a SKU category; null when SkuTestimonials should stay. */
export function getSkuPageTrustpilotVariant(
    category: string | null | undefined,
): SkuPageTrustpilotVariant | null {
    const normalized = category?.trim().toLowerCase() ?? ''
    if (normalized === 'lab') return 'lab'
    if (normalized === 'medication') return 'medications'
    if (PRODUCT_PAGE_TRUSTPILOT_CATEGORIES.has(normalized)) return 'product'
    return null
}

/** True when this SKU should render Trustpilot reviews on the product page. */
export function showsProductPageTrustpilot(category: string | null | undefined): boolean {
    return getSkuPageTrustpilotVariant(category) != null
}

/** Resolve the Trustpilot section id for a product-page variant. */
export function getSkuPageTrustpilotSectionId(variant: SkuPageTrustpilotVariant): string {
    return SECTION_ID_BY_VARIANT[variant]
}

function normalizeSectionId(sectionId: string): string {
    if (!sectionId || typeof sectionId !== 'string') return ''
    return sectionId.trim().toLowerCase().replace(/_/g, '-')
}

/** Resolve testimonialReviewsV2 copy for a rendered-service section id. */
export function getTestimonialReviewsV2Data(sectionId: string): TestimonialReviewsV2Data | null {
    const key = normalizeSectionId(sectionId)
    if (!key) return null
    return TESTIMONIAL_REVIEWS_V2_BY_ID[key] ?? null
}
